import { CalendarOutlined } from "@ant-design/icons"
import { Col, Row, Select } from "antd"
import { type Dayjs } from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "../overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../overview/OverviewCardIcon.tsx"

interface ScopePlanningValues {
  timeline: string | null
  budget_range: string | null
  start_date: string | null
  end_date: string | null
}

interface LeadScopePlanningCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadScopePlanningCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadScopePlanningCardProps) {
  const { t } = useTranslation()
  const { userFormat, parseDate, normalizeDate } = useDateFormat()

  const values: ScopePlanningValues = {
    timeline: lead?.timeline ?? null,
    budget_range: lead?.budget_range ?? null,
    start_date: lead?.start_date ?? null,
    end_date: lead?.end_date ?? null
  }

  const showDate = (value?: string | null) =>
    normalizeDate(value, userFormat ?? undefined)

  const labelOf = (
    options: LeadsFilterOptions[keyof LeadsFilterOptions],
    value?: string | null
  ) =>
    (options?.find((option) => option?.value === value)?.label as
      | string
      | undefined) ?? value

  const toIsoDay = (value: Dayjs | null) =>
    value?.isValid() ? value.format(DATE_FORMAT) : null

  return (
    <WorkspaceCard<ScopePlanningValues>
      cardId={"scope-planning"}
      title={
        <OverviewCardTitle icon={<CalendarOutlined />} accent={"orange"}>
          {t("leadWorkspace.scope.planning.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.scope.planning.title")}
      canEdit={canEdit}
      values={values}
      onSave={onPatch}
      renderEdit={({ values, setValues, saving }) => (
        <Row gutter={[16, 12]} className={"py-1"}>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWorkspace.cards.project.timeline")}
            >
              <Select
                autoFocus
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.timeline")}
                optionFilterProp={"label"}
                options={filterOptions?.timeline ?? []}
                value={values.timeline ?? undefined}
                onChange={(next) => setValues({ timeline: next ?? null })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWorkspace.scope.planning.startDate")}
            >
              <CustomDatePicker
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.scope.planning.startDate")}
                value={parseDate(values.start_date)}
                onChange={(next) => setValues({ start_date: toIsoDay(next) })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField
              label={t("leadWorkspace.scope.planning.endDate")}
            >
              <CustomDatePicker
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.scope.planning.endDate")}
                value={parseDate(values.end_date)}
                onChange={(next) => setValues({ end_date: toIsoDay(next) })}
              />
            </LeadOverviewField>
          </Col>
          <Col xs={24} sm={12}>
            <LeadOverviewField label={t("leadWorkspace.cards.project.budget")}>
              <Select
                allowClear
                className={"w-full"}
                disabled={saving}
                aria-label={t("leadWorkspace.cards.project.budget")}
                optionFilterProp={"label"}
                options={filterOptions?.budget_range ?? []}
                value={values.budget_range ?? undefined}
                onChange={(next) => setValues({ budget_range: next ?? null })}
              />
            </LeadOverviewField>
          </Col>
        </Row>
      )}
    >
      <Row gutter={[16, 12]} className={"py-1"}>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("leadWorkspace.cards.project.timeline")}
            value={labelOf(filterOptions?.timeline, lead?.timeline)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("leadWorkspace.scope.planning.startDate")}
            value={showDate(lead?.start_date)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("leadWorkspace.scope.planning.endDate")}
            value={showDate(lead?.end_date)}
          />
        </Col>
        <Col xs={24} sm={12}>
          <LeadOverviewField
            label={t("leadWorkspace.cards.project.budget")}
            value={labelOf(filterOptions?.budget_range, lead?.budget_range)}
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export { type ScopePlanningValues, LeadScopePlanningCard }
